import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from '../../i18n';
import { store } from '../../store';
import { icon } from './../ui/icons';

// Bump this when the wording / cookie usage materially changes — a new version
// re-shows the notice to everyone who previously dismissed an older one.
const NOTICE_VERSION = 'v1';
const STORAGE_KEY = 'cadence.cookieNotice';

/**
 * <cadence-cookie-notice> — a one-time informational cookie banner (Phase 5.8, GDPR).
 *
 * Cadence uses ONLY strictly-necessary cookies (the per-surface session cookies that
 * keep you signed in) — no analytics, tracking, or advertising. Strictly-necessary
 * cookies don't require opt-in consent under the ePrivacy Directive, so this is an
 * acknowledgement ("Got it"), not a consent gate: there is nothing to reject.
 *
 * Mounted globally from index.html so it overlays every surface (login, admin,
 * portal, onboarding) regardless of route. Dismissal is remembered in localStorage
 * (functional client storage, not a cookie), versioned so wording changes can re-show.
 */
@customElement('cadence-cookie-notice')
export class CadenceCookieNotice extends LitElement {
  @state() private visible = false;
  @state() private showDetails = false;
  private unsub?: () => void;

  static styles = css`
    :host { position: fixed; inset: auto 0 0 0; z-index: 1000; display: block; pointer-events: none; }
    .wrap { display: flex; justify-content: center; padding: var(--space-3); }
    .card {
      pointer-events: auto;
      display: flex; align-items: flex-start; gap: 14px;
      width: 100%; max-width: 760px;
      padding: 16px 18px;
      background: var(--color-bg-primary);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg, var(--radius-md));
      box-shadow: var(--shadow-lg);
      color: var(--color-text-primary);
    }
    .ico { flex-shrink: 0; color: var(--color-primary); display: inline-flex; margin-top: 2px; }
    .ico svg { width: 22px; height: 22px; }
    .body { flex: 1; min-width: 0; }
    .title { font-weight: 600; margin: 0 0 4px; }
    p { margin: 0; font-size: 0.9rem; color: var(--color-text-secondary); line-height: 1.5; }
    .more { background: none; border: none; padding: 0; margin-top: 6px; font: inherit; font-size: 0.85rem;
      color: var(--color-primary); cursor: pointer; }
    .more:hover { text-decoration: underline; }
    .details { margin: 10px 0 0; padding-left: 18px; font-size: 0.84rem; color: var(--color-text-secondary); line-height: 1.6; }
    .details li { margin: 0; }
    .actions { flex-shrink: 0; display: flex; align-items: center; }
    @media (max-width: 560px) {
      .card { flex-direction: column; }
      .actions { width: 100%; }
      .actions cadence-button { width: 100%; }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    let dismissed = false;
    try { dismissed = localStorage.getItem(STORAGE_KEY) === NOTICE_VERSION; } catch { /* storage unavailable */ }
    this.visible = !dismissed;
    // Re-render once boot resolves the locale (the notice mounts before that).
    this.unsub = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsub?.();
  }

  private accept() {
    try { localStorage.setItem(STORAGE_KEY, NOTICE_VERSION); } catch { /* storage unavailable — just hide for this session */ }
    this.visible = false;
  }

  render() {
    if (!this.visible) return null;
    return html`
      <div class="wrap">
        <div class="card" role="dialog" aria-label=${t('common.cookies.title')} aria-live="polite">
          <span class="ico">${icon('cookie', 22)}</span>
          <div class="body">
            <p class="title">${t('common.cookies.title')}</p>
            <p>${t('common.cookies.body')}</p>
            <button class="more" @click=${() => (this.showDetails = !this.showDetails)}>
              ${this.showDetails ? t('common.cookies.hide') : t('common.cookies.learnMore')}
            </button>
            ${this.showDetails ? html`
              <ul class="details">
                <li>${t('common.cookies.itemSession')}</li>
                <li>${t('common.cookies.itemPrefs')}</li>
                <li>${t('common.cookies.itemNoTracking')}</li>
              </ul>` : ''}
          </div>
          <div class="actions">
            <cadence-button @click=${() => this.accept()}>${t('common.cookies.accept')}</cadence-button>
          </div>
        </div>
      </div>`;
  }
}
